import React from "react";
import { MdClose, MdSave } from "react-icons/md";
import { adminUpdateUserProfile } from "../services/adminApi";
import type { UserListItem } from "../services/usersService";

type Props = {
  open: boolean;
  onClose: () => void;
  user: UserListItem | null;
  canEdit: boolean;
  onSaved: (patch: Partial<UserListItem>) => void;
};

export default function UserEditorDrawer({
  open,
  onClose,
  user,
  canEdit,
  onSaved,
}: Props): React.ReactElement | null {
  const [fullName, setFullName] = React.useState<string>("");
  const [phone, setPhone] = React.useState<string>("");
  const [address, setAddress] = React.useState<string>("");
  const [age, setAge] = React.useState<string>(""); // mantener como string para manejar vacío
  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  // hidratar al abrir
  React.useEffect(() => {
    if (!open) return;
    setFullName(user?.fullName ?? "");
    setPhone((user as any)?.phone ?? "");
    setAddress((user as any)?.address ?? "");
    setAge(
      typeof (user as any)?.age === "number" && !Number.isNaN((user as any).age)
        ? String((user as any).age)
        : ""
    );
    setError(null);
  }, [open, user]);

  if (!open || !user) return null;

  const close = () => {
    if (saving) return;
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;

    // Construye patch SIN undefined (importante por exactOptionalPropertyTypes)
    const patch: Partial<UserListItem> = {
      ...(fullName.trim().length ? { fullName: fullName.trim() } : {}),
      ...(phone.trim().length ? { phone: phone.trim() } : {}),
      ...(address.trim().length ? { address: address.trim() } : {}),
      ...(age.trim().length && !Number.isNaN(Number(age))
        ? { age: Number(age) as unknown as never } // cast leve para modelos amplios
        : {}),
    };

    try {
      setSaving(true);
      await adminUpdateUserProfile(user.uid, patch as Record<string, unknown>);
      onSaved(patch);
      onClose();
    } catch (e2) {
      setError((e2 as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={close}
        aria-hidden
      />
      {/* drawer */}
      <aside className="absolute right-0 top-0 h-dvh w-full max-w-md bg-surface shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-surface px-4 py-3">
          <h3 className="text-lg font-semibold">Editar usuario</h3>
          <button
            className="btn btn-outline flex items-center gap-1"
            onClick={close}
            disabled={saving}
            aria-label="Cerrar"
          >
            <MdClose /> Cerrar
          </button>
        </div>

        {/* Contenido scrolleable */}
        <form className="flex-1 overflow-y-auto px-4 py-4" onSubmit={submit}>
          <Field label="UID">
            <code className="rounded bg-black/5 px-2 py-1 text-xs dark:bg-white/10">
              {user.uid}
            </code>
          </Field>

          <Field label="Email">
            <input
              className="w-full cursor-not-allowed rounded border border-surface bg-black/5 px-3 py-2 text-on-surface/70 dark:bg-white/10"
              value={user.email}
              readOnly
              aria-readonly="true"
            />
          </Field>

          <Field label="Nombre completo">
            <input
              className="w-full rounded border border-surface bg-surface px-3 py-2"
              value={fullName}
              onChange={(e) => setFullName(e.currentTarget.value)}
              placeholder="Nombre y apellidos"
            />
          </Field>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Teléfono">
              <input
                className="w-full rounded border border-surface bg-surface px-3 py-2"
                value={phone}
                onChange={(e) => setPhone(e.currentTarget.value)}
                inputMode="tel"
                placeholder="+52 …"
              />
            </Field>
            <Field label="Edad">
              <input
                className="w-full rounded border border-surface bg-surface px-3 py-2"
                value={age}
                onChange={(e) => setAge(e.currentTarget.value)}
                inputMode="numeric"
                placeholder="Ej. 28"
              />
            </Field>
          </div>

          <Field label="Dirección">
            <textarea
              className="h-24 w-full rounded border border-surface bg-surface px-3 py-2"
              value={address}
              onChange={(e) => setAddress(e.currentTarget.value)}
              placeholder="Calle, número, ciudad…"
            />
          </Field>

          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </form>

        {/* Footer SIEMPRE visible (no scrollea) */}
        <div className="shrink-0 border-t border-surface bg-surface px-4 py-3">
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="btn btn-outline"
              onClick={close}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="" // el botón dispara el primer <form> ancestro
              onClick={(e) => {
                // asegura que el submit vaya al form de arriba
                const form = (
                  e.currentTarget.closest("aside") as HTMLElement
                )?.querySelector("form") as HTMLFormElement | null;
                form?.requestSubmit();
              }}
              className="btn btn-primary flex items-center gap-1"
              disabled={saving || !canEdit}
              title={!canEdit ? "No tienes permisos para editar" : "Guardar"}
            >
              <MdSave />
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

/* helpers UI */
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1 mb-3">
      <label className="block text-xs text-on-surface/70">{label}</label>
      <div>{children}</div>
    </div>
  );
}
